import { action, computed, observable, runInAction } from 'mobx';
// eslint-disable-next-line import/default
import moment from 'moment';

import { CardBank, CardType, IFeeComparison, Providers, Section } from '../contracts';
import { IFxRateService } from '../services/contracts';

export interface IAmount {
    currency: string;
    value: number;
}

export class Store {

    @observable
    isCalculating: boolean = false;

    @observable
    cardType: CardType = CardType.Visa;

    @observable
    paidAmount: IAmount;

    @observable
    bankChargeAmount: IAmount;

    @observable
    additionalChargeAmount: IAmount;

    @observable
    yearlyBudget: IAmount;

    @observable
    currentSection: Section = Section.CalculatorForm;

    @observable
    paymentDate: Date = moment.utc().add(-1, 'd').toDate();

    @observable
    visaRate = 1;

    @observable
    mastercardRate = 1;

    @computed
    get payhawkRate() {
        return this.visaRate * 1.005;
    }

    @computed
    get spendDeskRate() {
        return this.mastercardRate * 1.0299;
    }

    @computed
    get estimatedVisaCharge() {
        return this.paidAmount.value * this.visaRate;
    }

    @computed
    get estimatedMastercardCharge() {
        return this.paidAmount.value * this.mastercardRate;
    }

    @computed
    get estimatedPayhawkCharge() {
        return this.paidAmount.value * this.payhawkRate;
    }

    @computed
    get estimatedSpendDeskCharge() {
        return this.paidAmount.value * this.spendDeskRate;
    }

    @computed
    get estimatedBankFees() {
        if (this.cardType === CardType.Visa) {
            return this.totalBankCharge - this.estimatedVisaCharge;
        } else {
            return this.totalBankCharge - this.estimatedMastercardCharge;
        }
    }

    @computed
    get estimatedBankFeesPercentage() {
        if (this.totalBankCharge <= 0) {
            return 0;
        }

        return this.estimatedBankFees * 100 / this.totalBankCharge;
    }

    @computed
    get estimatedPayhawkFees() {
        return this.estimatedPayhawkCharge - this.estimatedVisaCharge;
    }

    @computed
    get estimatedPayhawkYearlyFees(): number {
        return this.yearlyBudget.value * (this.estimatedPayhawkFees / this.totalBankCharge);
    }

    @computed
    get estimatedBankYearlyFees(): number {
        return this.yearlyBudget.value * (this.estimatedBankFees / this.totalBankCharge);
    }

    @computed
    get estimatedSpendDeskFees() {
        return this.estimatedSpendDeskCharge - this.estimatedMastercardCharge;
    }

    @computed
    get estimatedPayhawkVersusBankFees() {
        const a = this.estimatedBankFees / this.estimatedPayhawkFees;
        return Math.round(a) || 1;
    }

    @computed
    get estimatedPayhawkVersusSpendesk() {
        const a = this.estimatedSpendDeskFees / this.estimatedPayhawkFees;
        return Math.round(a) || 1;
    }

    @computed
    get totalBankCharge() {
        return this.bankChargeAmount.value + this.additionalChargeAmount.value;
    }

    @computed
    get feeComparisons(): IFeeComparison[] {
        const fees = [{
            type: CardBank.YourBank,
            amount: this.estimatedBankFees,
        }, {
            type: CardBank.Payhawk,
            amount: this.estimatedPayhawkFees,
        }, {
            type: CardBank.Spendesk,
            amount: this.estimatedSpendDeskFees,
        }];

        return fees.sort((f1, f2) => {
            if (f1.amount < f2.amount) {
                return -1;
            } else if (f1.amount === f2.amount) {
                return 0;
            } else {
                return 1;
            }
        });
    }

    constructor(private readonly fxRateService: IFxRateService) {
        this.paidAmount = {
            value: 0,
            currency: 'USD',
        };

        this.bankChargeAmount = {
            value: 0,
            currency: 'EUR',
        };

        this.additionalChargeAmount = {
            value: 0,
            currency: 'EUR',
        };

        this.yearlyBudget = {
            value: 10000,
            currency: 'EUR',
        };
    }

    @action.bound
    setCardType(type: string) {
        this.cardType = type as CardType;
    }

    @action.bound
    setPaidAmount(amount: IAmount) {
        this.paidAmount = amount;
    }

    @action.bound
    setBankCharge(amount: IAmount) {
        this.bankChargeAmount = amount;
        this.additionalChargeAmount.currency = amount.currency;
        this.yearlyBudget.currency = amount.currency;
    }

    @action.bound
    setAdditionalCharge(amount: IAmount) {
        this.additionalChargeAmount = amount;
    }

    @action.bound
    setYearlyBudget(amount: IAmount) {
        this.yearlyBudget = amount;
    }

    @action.bound
    async calculateFees() {
        this.isCalculating = true;

        const visaRate = await this.fxRateService.get(this.paidAmount.currency, this.bankChargeAmount.currency, this.paymentDate!, Providers.Visa) || 1;
        const mastercardRate = await this.fxRateService.get(this.paidAmount.currency, this.bankChargeAmount.currency, this.paymentDate!, Providers.Mastercard) || 1;

        runInAction(() => {
            this.isCalculating = false;
            this.visaRate = visaRate;
            this.mastercardRate = mastercardRate;
            this.currentSection = Section.Breakdown;
        });
    }

    @action.bound
    goToBankCompare() {
        this.currentSection = Section.BankComparison;
    }

    @action.bound
    goToYearlyBudget() {
        this.currentSection = Section.YearlyBudget;
    }

    @action.bound
    setDate(date: Date) {
        this.paymentDate = date;
    }

    @action.bound
    switchAmounts() {
        const paidAmount = this.paidAmount;
        this.paidAmount = this.bankChargeAmount;
        this.bankChargeAmount = paidAmount;
    }

    @action.bound
    goBack() {
        if (this.currentSection <= 0) {
            return;
        }

        this.currentSection = this.currentSection - 1;
    }
}